import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import { useEffect } from 'react';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { StarRating } from './StarRating';
import { Button } from './ui/button';
import { Link } from 'react-router-dom';
import { formatPrice, getCityName } from '../lib/constants';

// Fix Leaflet default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

function createCustomIcon(imageUrl, name) {
  return L.divIcon({
    className: 'custom-marker-container',
    html: `
      <div style="
        width: 44px;
        height: 44px;
        border-radius: 50%;
        border: 3px solid #2D6A4F;
        overflow: hidden;
        background: white;
        box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      ">
        ${imageUrl 
          ? `<img src="${imageUrl}" alt="${name}" style="width: 100%; height: 100%; object-fit: cover;" />`
          : `<div style="width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; background: #2D6A4F; color: white; font-weight: bold; font-size: 16px;">${name?.charAt(0) || 'P'}</div>`
        }
      </div>
    `,
    iconSize: [44, 44],
    iconAnchor: [22, 44],
    popupAnchor: [0, -44],
  });
}

function MapUpdater({ center, zoom }) {
  const map = useMap();
  
  useEffect(() => {
    if (center) {
      map.setView(center, zoom || map.getZoom());
    }
  }, [center, zoom, map]);
  
  return null;
}

export function ProvidersMap({ providers = [], center = [38.7223, -9.1393], zoom = 12, height = '400px' }) {
  return (
    <div className="rounded-2xl overflow-hidden shadow-sm border" style={{ height }}>
      <MapContainer
        center={center}
        zoom={zoom}
        style={{ height: '100%', width: '100%' }}
        scrollWheelZoom={true}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <MapUpdater center={center} zoom={zoom} />
        
        {providers.map((provider) => {
          if (!provider.latitude || !provider.longitude) return null;
          
          const user = provider.user || {};
          const minPrice = provider.services?.length > 0
            ? Math.min(...provider.services.map(s => s.price_per_hour || s.price_per_day || 999))
            : null;
          
          return (
            <Marker
              key={provider.provider_id}
              position={[provider.latitude, provider.longitude]}
              icon={createCustomIcon(user.picture, user.name)}
            >
              <Popup>
                <div className="p-2 min-w-[200px]">
                  <div className="flex items-center gap-3">
                    <Avatar className="h-12 w-12 border-2 border-primary/20">
                      <AvatarImage src={user.picture} alt={user.name} />
                      <AvatarFallback>{user.name?.charAt(0) || 'P'}</AvatarFallback>
                    </Avatar>
                    <div>
                      <h4 className="font-semibold">{user.name}</h4>
                      <StarRating rating={provider.rating || 0} size="sm" />
                    </div>
                  </div>
                  {minPrice && (
                    <p className="mt-2 text-sm">
                      Desde <span className="font-semibold text-primary">{formatPrice(minPrice)}/h</span>
                    </p>
                  )}
                  <Link to={`/client/providers/${provider.provider_id}`}>
                    <Button size="sm" className="w-full mt-3 rounded-full">
                      Ver Perfil
                    </Button>
                  </Link>
                </div>
              </Popup>
            </Marker>
          );
        })}
      </MapContainer>
    </div>
  );
}
